import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Rocket, Star, Clock, Zap, TrendingUp, CheckCircle, ArrowLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const PROMOTION_OPTIONS = [
  {
    id: 'bump',
    name: 'Bump Listing',
    description: 'Move your listing to the top of search results',
    price: 0.99,
    duration: '24 hours',
    icon: TrendingUp,
    color: 'blue',
    popular: false
  },
  {
    id: 'boost',
    name: 'Boost Visibility',
    description: 'Get 3x more views with highlighted placement',
    price: 2.99,
    duration: '7 days',
    icon: Rocket,
    color: 'purple',
    popular: true
  },
  {
    id: 'feature',
    name: 'Feature on Homepage',
    description: 'Showcase your item in the Featured section',
    price: 4.99,
    duration: '7 days',
    icon: Star,
    color: 'amber',
    popular: false
  },
  {
    id: 'urgent',
    name: 'Urgent Sale Badge',
    description: 'Add urgency with a "Quick Sale" badge',
    price: 1.99,
    duration: '3 days',
    icon: Zap,
    color: 'red',
    popular: false
  }
];

const PromotePage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { products, user, isAuthenticated } = useApp();
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    // Try to find product in local context first
    if (productId && products.length > 0) {
      const found = products.find(p => p.id === productId);
      if (found) {
        setProduct(found);
        return;
      }
    }
    
    // If not in context and we have a valid UUID, fetch from API
    if (productId && productId.length > 10 && !productId.includes('test')) {
      const fetchProduct = async () => {
        try {
          const res = await fetch(`${API_BASE}/api/products/${productId}`);
          if (res.ok) {
            const data = await res.json();
            setProduct(data);
          }
        } catch (err) {
          console.error('Failed to fetch product:', err);
        }
      };
      fetchProduct();
    }
  }, [productId, products]);
  
  // Validate productId format - must be a valid UUID
  const isValidProductId = productId && 
    productId.length >= 30 && 
    !productId.toLowerCase().includes('test') &&
    !productId.toLowerCase().includes('undefined');

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Rocket className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign in to promote listings</h2>
          <Button onClick={() => navigate('/login')} className="bg-minted-600 hover:bg-minted-700">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  const handlePurchase = async () => {
    if (!selectedPromotion) {
      toast.error('Please select a promotion option');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/promotions/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          product_id: productId,
          promotion_type: selectedPromotion.id,
          price: selectedPromotion.price
        })
      });

      const data = await response.json();
      
      if (data.success && data.checkout_url) {
        toast.success('Redirecting to payment...');
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || 'Failed to purchase promotion');
      }
    } catch (error) {
      console.error('Promotion purchase error:', error);
      toast.error('Failed to purchase promotion. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-lg mx-auto flex items-center h-14 px-4">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 mr-2">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold">Promote Listing</h1>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Invalid Product Warning */}
        {!isValidProductId && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <h3 className="font-medium text-red-800 mb-1">Invalid Product</h3>
            <p className="text-sm text-red-600">
              This promotion link appears to be invalid. Please go back to your listings and try promoting again.
            </p>
            <Button 
              onClick={() => navigate('/my-listings')} 
              variant="outline"
              className="mt-3 text-red-700 border-red-300 hover:bg-red-100"
            >
              Go to My Listings
            </Button>
          </div>
        )}

        {/* Product Preview */}
        {product && isValidProductId && (
          <div className="bg-white rounded-xl p-4 mb-6 flex items-center gap-4">
            <img
              src={product.images?.[0] || 'https://via.placeholder.com/80'}
              alt={product.title}
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate">{product.title}</h3>
              <p className="text-minted-600 font-bold">${product.price}</p>
            </div>
          </div>
        )}

        {/* Promotion Options - only show if valid product */}
        {isValidProductId && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Choose a promotion</h2>
        
        <div className="space-y-3">
          {PROMOTION_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = selectedPromotion?.id === option.id;
            
            const colorClasses = {
              blue: 'bg-blue-100 text-blue-600',
              purple: 'bg-purple-100 text-purple-600',
              amber: 'bg-amber-100 text-amber-600',
              red: 'bg-red-100 text-red-600'
            };

            return (
              <button
                key={option.id}
                onClick={() => setSelectedPromotion(option)}
                className={`w-full bg-white rounded-xl p-4 text-left transition-all border-2 ${
                  isSelected 
                    ? 'border-minted-600 ring-2 ring-minted-100' 
                    : 'border-transparent hover:border-gray-200'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[option.color]}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{option.name}</h3>
                      {option.popular && (
                        <span className="text-xs bg-minted-100 text-minted-700 px-2 py-0.5 rounded-full font-medium">
                          Popular
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        {option.duration}
                      </div>
                      <span className="font-bold text-lg text-gray-900">${option.price.toFixed(2)}</span>
                    </div>
                  </div>
                  {isSelected && (
                    <CheckCircle className="w-6 h-6 text-minted-600 flex-shrink-0" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Purchase Button */}
        <div className="mt-6 sticky bottom-4">
          <Button
            onClick={handlePurchase}
            disabled={!selectedPromotion || loading || !isValidProductId}
            className="w-full bg-minted-600 hover:bg-minted-700 h-12 text-base"
          >
            {loading ? 'Processing...' : selectedPromotion 
              ? `Pay $${selectedPromotion.price.toFixed(2)} with Stripe` 
              : 'Select a promotion'}
          </Button>
          <p className="text-xs text-gray-400 text-center mt-2">
            Secure payment powered by Stripe
          </p>
        </div>

        {/* Benefits */}
        <div className="mt-8 bg-minted-50 rounded-xl p-4">
          <h3 className="font-semibold text-minted-900 mb-3">Why promote your listing?</h3>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-sm text-minted-700">
              <CheckCircle className="w-4 h-4 text-minted-600" />
              Sell up to 5x faster
            </li>
            <li className="flex items-center gap-2 text-sm text-minted-700">
              <CheckCircle className="w-4 h-4 text-minted-600" />
              Get more views and messages
            </li>
            <li className="flex items-center gap-2 text-sm text-minted-700">
              <CheckCircle className="w-4 h-4 text-minted-600" />
              Stand out from other listings
            </li>
          </ul>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PromotePage;
